import React from 'react';
import { Box, Typography, Chip, Tooltip, Skeleton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LanguageIcon from '@mui/icons-material/Language';
import LayersIcon from '@mui/icons-material/Layers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import {
  getAuthorsDisplay,
  getCopyrightHoldersCount,
  getLicenseInfo,
} from '../utils/metadataUtils';

function DocumentMetadata({ doc }) {
  const metadata = doc.classificationData?.document?.metadata;
  const chunks = doc.classificationData?.chunks || [];
  const pageCount = doc.classificationData?.document?.page_count;
  const accessibility = doc.classificationData?.accessibility;
  const isLoading = doc.status === 'uploading' || doc.status === 'classifying';

  const renderAccessibilityChip = () => {
    if (!accessibility) return null;

    if (accessibility.error && !accessibility.compliant) {
      return (
        <Tooltip title={`Validation Error: ${accessibility.error}`}>
          <Chip
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            label="A11y: Error"
            size="small"
            color="warning"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Tooltip>
      );
    }

    if (accessibility.compliant || accessibility.rules_violated === 0) {
      return (
        <Tooltip title={`${accessibility.summary} (${accessibility.profile_name})`}>
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '0.9rem' }} />}
            label="A11y: Compliant"
            size="small"
            color="success"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`${accessibility.summary} (${accessibility.profile_name})`}>
        <Chip
          icon={<ErrorIcon sx={{ fontSize: '0.9rem' }} />}
          label={`A11y: ${accessibility.rules_violated} issues`}
          size="small"
          color="error"
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      </Tooltip>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="60%" height={28} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="40%" height={20} />
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={80} height={22} />
          <Skeleton variant="rounded" width={100} height={22} />
          <Skeleton variant="rounded" width={70} height={22} />
          <Skeleton variant="rounded" width={90} height={22} />
        </Box>
      </Box>
    );
  }

  if (!metadata) {
    return (
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc.filename}
        </Typography>
        {(chunks.length > 0 || accessibility) && (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chunks.length > 0 && (
              <Chip
                icon={<LayersIcon sx={{ fontSize: '0.9rem' }} />}
                label={`${chunks.length} chunks`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
            {renderAccessibilityChip()}
          </Box>
        )}
      </Box>
    );
  }

  const authorsDisplay = getAuthorsDisplay(metadata);
  const copyrightHoldersCount = getCopyrightHoldersCount(metadata);
  const licenseInfo = getLicenseInfo(metadata);

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {metadata.title || doc.filename}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.25 }}>
        <DescriptionIcon sx={{ fontSize: '0.8rem' }} />
        {doc.filename}
      </Typography>
      {authorsDisplay && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <PersonIcon sx={{ fontSize: '0.8rem' }} />
          {authorsDisplay}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {metadata.pubType && (
          <Chip
            icon={<MenuBookIcon sx={{ fontSize: '0.9rem' }} />}
            label={metadata.pubType}
            size="small"
            color="primary"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {metadata.isbn && (
          <Chip
            label={`ISBN: ${metadata.isbn}`}
            size="small"
            variant="outlined"
            color="default"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {metadata.copyright_year && (
          <Chip
            icon={<CalendarTodayIcon sx={{ fontSize: '0.9rem' }} />}
            label={metadata.copyright_year}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {metadata.language_term && (
          <Chip
            icon={<LanguageIcon sx={{ fontSize: '0.9rem' }} />}
            label={metadata.language_term}
            size="small"
            variant="outlined"
            color="info"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {pageCount && (
          <Chip
            label={`${pageCount} pages`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {chunks.length > 0 && (
          <Chip
            icon={<LayersIcon sx={{ fontSize: '0.9rem' }} />}
            label={`${chunks.length} chunks`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {copyrightHoldersCount > 0 && (
          <Chip
            icon={<PersonIcon sx={{ fontSize: '0.9rem' }} />}
            label={`${copyrightHoldersCount} holders`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {licenseInfo.label && (
          <Chip
            icon={licenseInfo.isOpen ? <LockOpenIcon sx={{ fontSize: '0.9rem' }} /> : <LockIcon sx={{ fontSize: '0.9rem' }} />}
            label={licenseInfo.label}
            size="small"
            color={licenseInfo.isOpen ? "success" : "default"}
            variant={licenseInfo.isOpen ? "filled" : "outlined"}
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {metadata.digital_asset_collection_key && (
          <Chip
            icon={<FolderIcon sx={{ fontSize: '0.9rem' }} />}
            label={metadata.digital_asset_collection_key}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {metadata.typesetting && (
          <Chip
            label={`Typesetting: ${metadata.typesetting}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {renderAccessibilityChip()}
      </Box>
    </Box>
  );
}

export default DocumentMetadata;